"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getRefiPayoff = getRefiPayoff;
exports.getAppliedDebtSwapPayoff = getAppliedDebtSwapPayoff;
exports.quoteOffer = quoteOffer;
exports.lineSizeForNetCashCents = lineSizeForNetCashCents;
const assertUtils_1 = require("../assertUtils");
const offer_1 = require("./offer");
const math_1 = require("./slider/math");
const pricingBands_1 = require("./slider/pricingBands");
const toCents = (dollars) => (0, math_1.roundDecimalProduct)(dollars, 100);
const roundHalfEvenSum = (products) => (0, math_1.roundHalfEven)((0, math_1.sumOfProducts)(products));
/** With no required upfront draw, Python's estimates assume the applicant draws the full line. */
const FULL_LINE_DRAWN = 1;
function fail(offer, message) {
    throw new Error(`Offer ${offer.policyName}${offer.identifier ? ` (${offer.identifier})` : ''}: ${message}`);
}
function required(offer, field, value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        fail(offer, `missing ${field}`);
    }
    return value;
}
/** Principal of every refinanced lien: no fees, no buffers. Zero when the offer does not refinance. */
function getRefiPayoff(terms) {
    const refi = terms.lienRefiOfferTerms;
    if (!refi) {
        if (terms.contingencies?.includes(offer_1.OfferContingency.priorLienRelease)) {
            fail(terms, 'refinance offer has no lienRefiOfferTerms');
        }
        return { totalCents: 0, liens: [] };
    }
    const liens = refi.refiLiensDetails.map((lien) => ({
        lienPosition: lien.lienPosition,
        balanceCents: toCents(lien.balanceAmount),
        lenderName: lien.lenderName,
    }));
    const totalCents = liens.reduce((sum, lien) => sum + lien.balanceCents, 0);
    if (totalCents !== toCents(refi.totalBalance)) {
        fail(terms, `lienRefiOfferTerms.totalBalance ${refi.totalBalance} does not match its liens (${totalCents} cents)`);
    }
    return { totalCents, liens };
}
/** Balances of the debt swap underwriting applied to the offer, never the user's current selection. */
function getAppliedDebtSwapPayoff(terms) {
    const debtSwap = terms.appliedDebtSwapOfferTerms;
    if (!debtSwap) {
        if (terms.appliedSelectedDebtSwapPersonalFinancialLiabilityIds?.length) {
            // An offer stored before the terms existed: reading no debt swap would overstate the cash to the applicant.
            fail(terms, 'offer applied a debt swap (appliedSelectedDebtSwapPersonalFinancialLiabilityIds) but has no appliedDebtSwapOfferTerms');
        }
        return { totalCents: 0, tradelines: [] };
    }
    const tradelines = debtSwap.tradelines.map((tradeline) => ({
        personalFinancialLiabilityId: tradeline.personalFinancialLiabilityId,
        balanceCents: toCents(tradeline.balanceAmount),
        lenderName: tradeline.lenderName,
    }));
    const totalCents = tradelines.reduce((sum, tradeline) => sum + tradeline.balanceCents, 0);
    if (totalCents !== toCents(debtSwap.totalBalance)) {
        fail(terms, `appliedDebtSwapOfferTerms.totalBalance ${debtSwap.totalBalance} does not match its tradelines (${totalCents} cents)`);
    }
    return { totalCents, tradelines };
}
function readQuoteInputs(offer) {
    const originationFeePercent = required(offer, 'originationFeePercent', offer.originationFeePercent);
    // Python leaves the first-draw fee null on offers that are not fixed-term; there is no first draw to charge.
    const firstDrawFeePercent = offer.fixedTermMonths == null ? (offer.firstDrawFeePercent ?? 0) : required(offer, 'firstDrawFeePercent', offer.firstDrawFeePercent);
    for (const [name, percent] of [
        ['originationFeePercent', originationFeePercent],
        ['firstDrawFeePercent', firstDrawFeePercent],
    ]) {
        if (percent < 0 || percent >= 1) {
            fail(offer, `${name} ${percent} must be a ratio in [0, 1)`);
        }
    }
    // Which recording policy a band follows decides real money, so an offer that predates the flag is not guessed at.
    if (offer.lineSizePricingBands?.some((band) => typeof band.recordingTaxFree !== 'boolean')) {
        fail(offer, 'a pricing band is missing recordingTaxFree');
    }
    return {
        offer,
        refiPayoff: getRefiPayoff(offer),
        debtSwapPayoff: getAppliedDebtSwapPayoff(offer),
        originationFeePercent,
        firstDrawFeePercent,
        recordingFixedCents: toCents(required(offer, 'recordingChargesFixedComponentDollars', offer.recordingChargesFixedComponentDollars)),
        recordingVariableDecimal: required(offer, 'recordingChargesVariableComponentDecimal', offer.recordingChargesVariableComponentDecimal),
        // Absent on offers that predate signing charges; Python holds the billed signing charge at 0 today.
        effectiveSigningCents: toCents(offer.effectiveSigningCharges ?? 0),
    };
}
/** The fraction of the line Python's `_getUpfrontDrawAmount` draws. A fixed-amount draw has none, so the inverse cannot use it. */
function fractionOfLineDrawn(offer) {
    const config = offer.upfrontDrawConfig;
    if (config == null) {
        return FULL_LINE_DRAWN;
    }
    switch (config.upfrontCashOutAmountCalculationMethod) {
        case offer_1.UpfrontCashOutAmountCalculationMethod.NoUpfrontCashOut:
            return FULL_LINE_DRAWN;
        case offer_1.UpfrontCashOutAmountCalculationMethod.PercentageOfLineSize:
            return config.minLineDrawDownForActivation;
        case offer_1.UpfrontCashOutAmountCalculationMethod.SpecificAmount:
            return fail(offer, 'a SpecificAmount draw does not scale with the line, so no line size can be derived from cash');
        default:
            return (0, assertUtils_1.assertNever)(config, `Offer ${offer.identifier}: unknown upfront draw config ${JSON.stringify(config)}`);
    }
}
/** Python `_getUpfrontDrawAmount`: the one draw every fee, cash and principal figure is based on. */
function computeDrawAmountCents({ offer }, lineSizeCents) {
    const config = offer.upfrontDrawConfig;
    const method = config?.upfrontCashOutAmountCalculationMethod;
    let drawAmountCents;
    switch (config?.upfrontCashOutAmountCalculationMethod) {
        case undefined:
        case offer_1.UpfrontCashOutAmountCalculationMethod.NoUpfrontCashOut:
            drawAmountCents = lineSizeCents;
            break;
        case offer_1.UpfrontCashOutAmountCalculationMethod.PercentageOfLineSize:
            drawAmountCents = (0, math_1.roundDecimalProduct)(lineSizeCents, config.minLineDrawDownForActivation);
            break;
        case offer_1.UpfrontCashOutAmountCalculationMethod.SpecificAmount:
            drawAmountCents = toCents(config.requiredDrawAmountForActivation);
            break;
        default:
            return fail(offer, `unknown upfront draw method ${String(method)}`);
    }
    const isUpfrontCashOut = config != null && method !== offer_1.UpfrontCashOutAmountCalculationMethod.NoUpfrontCashOut;
    if (drawAmountCents > lineSizeCents || (isUpfrontCashOut && drawAmountCents <= 0)) {
        fail(offer, `upfront draw ${drawAmountCents} cents is outside (0, line ${lineSizeCents} cents]`);
    }
    return drawAmountCents;
}
/**
 * Recording billed at a line size. A banded offer computes it per band from the components, with a recordingTaxFree
 * band billing none. A non-banded offer quoted at its own line takes Python's billed `effectiveRecordingCharges`, which
 * already applies the state recording-tax waiver (STATE_TAX_LINE_SIZE_ZERO_TAX_CUTOFF, getEffectiveOfferClosingCharges_).
 */
function computeRecordingFeeCents(inputs, lineSizeCents, band) {
    const { offer } = inputs;
    if (band == null && typeof offer.lineSize === 'number' && lineSizeCents === toCents(offer.lineSize)) {
        return toCents(required(offer, 'effectiveRecordingCharges', offer.effectiveRecordingCharges));
    }
    if (band?.recordingTaxFree) {
        return 0;
    }
    // Python getClosingChargesForLineSize: fixed + variable * line, rounded once. For a non-banded offer at a line other
    // than its own this is not what Python bills (it would not apply the waiver): Python only prices the offer's own line.
    return roundHalfEvenSum([
        [lineSizeCents, inputs.recordingVariableDecimal],
        [inputs.recordingFixedCents, 1],
    ]);
}
/** The draw as exact factors: Python never rounds a percentage draw, only the cash figures computed from it. */
function drawFactors({ offer }, lineSizeCents, drawAmountCents) {
    const config = offer.upfrontDrawConfig;
    return config?.upfrontCashOutAmountCalculationMethod === offer_1.UpfrontCashOutAmountCalculationMethod.PercentageOfLineSize ? [lineSizeCents, config.minLineDrawDownForActivation] : [drawAmountCents];
}
/** Fees, closing charges and net cash at a line size. Python `calcCashOutAfterPayoffAndFees`: both fees always apply. */
function computeCharges(inputs, lineSizeCents, band) {
    const drawAmountCents = computeDrawAmountCents(inputs, lineSizeCents);
    const draw = drawFactors(inputs, lineSizeCents, drawAmountCents);
    const upfrontFeesCents = roundHalfEvenSum([
        [...draw, inputs.firstDrawFeePercent],
        [lineSizeCents, inputs.originationFeePercent],
    ]);
    const originationFeeCents = roundHalfEvenSum([[lineSizeCents, inputs.originationFeePercent]]);
    const firstDrawFeeCents = upfrontFeesCents - originationFeeCents;
    const recordingFeeCents = computeRecordingFeeCents(inputs, lineSizeCents, band);
    const closingChargesCents = recordingFeeCents + inputs.effectiveSigningCents;
    // Python rounds this whole sum once rather than each fee, so a half-cent tie settles the same way.
    const netCashBeforeDebtSwapCents = roundHalfEvenSum([
        draw,
        [...draw, -inputs.firstDrawFeePercent],
        [lineSizeCents, -inputs.originationFeePercent],
        [-closingChargesCents, 1],
        [-inputs.refiPayoff.totalCents, 1],
    ]);
    const netCashCents = netCashBeforeDebtSwapCents - inputs.debtSwapPayoff.totalCents;
    return {
        drawAmountCents,
        upfrontFeesCents,
        originationFeeCents,
        firstDrawFeeCents,
        recordingFeeCents,
        closingChargesCents,
        netCashCents,
    };
}
/** The band for the line size. A banded offer must have one: a line outside every band is not priceable. */
function resolvePricingBand(offer, lineSizeCents) {
    if (!offer.lineSizePricingBands?.length) {
        return null;
    }
    return (0, pricingBands_1.resolveLineSizePricingBandForLineSizeCents)(offer.lineSizePricingBands, lineSizeCents) ?? fail(offer, `line size ${lineSizeCents} cents is outside every pricing band`);
}
function offerPricing(offer) {
    return {
        apr: required(offer, 'apr', offer.apr),
        margin: required(offer, 'margin', offer.margin),
        aprPreDiscount: offer.aprPreDiscount ?? null,
        fixedTermApr: offer.fixedTermApr ?? null,
        fixedTermAprPreDiscount: offer.fixedTermAprPreDiscount ?? null,
        primePromoApr: offer.primePromoApr ?? null,
        primePromoMargin: offer.primePromoMargin ?? null,
        primePromoAprPreDiscount: offer.primePromoAprPreDiscount ?? null,
    };
}
/**
 * Every figure of the offer at a line size, throwing rather than defaulting a missing input.
 * The state recording-tax waiver (STATE_TAX_LINE_SIZE_ZERO_TAX_CUTOFF) reaches the quote through banded offers'
 * recordingTaxFree bands; a non-banded offer quoted at its own line takes Python's billed effectiveRecordingCharges.
 */
function quoteOffer(offer, lineSizeCents) {
    if (!Number.isInteger(lineSizeCents) || lineSizeCents <= 0) {
        fail(offer, `line size ${lineSizeCents} cents must be a positive whole number of cents`);
    }
    const inputs = readQuoteInputs(offer);
    const pricingBand = resolvePricingBand(offer, lineSizeCents);
    const pricing = pricingBand?.pricing ?? offerPricing(offer);
    const charges = computeCharges(inputs, lineSizeCents, pricingBand);
    if (charges.netCashCents < 0) {
        fail(offer, `net cash is negative (${charges.netCashCents} cents) at line size ${lineSizeCents} cents`);
    }
    let fixedTermMonthlyPaymentCents = null;
    if (offer.fixedTermMonths == null) {
        if (pricing.fixedTermApr != null) {
            fail(offer, 'has a fixedTermApr but no fixedTermMonths');
        }
    }
    else {
        const fixedTermApr = required(offer, 'fixedTermApr', pricing.fixedTermApr);
        if (!Number.isInteger(offer.fixedTermMonths) || offer.fixedTermMonths <= 0) {
            fail(offer, `fixedTermMonths ${offer.fixedTermMonths} must be a positive integer`);
        }
        // Python upfrontDrawPrincipal: what is drawn less what is paid out of it for closing.
        fixedTermMonthlyPaymentCents = (0, math_1.computeMonthlyPaymentCents)(charges.drawAmountCents - charges.closingChargesCents, fixedTermApr, offer.fixedTermMonths);
    }
    return {
        lineSizeCents,
        ...charges,
        refiPayoff: inputs.refiPayoff,
        debtSwapPayoff: inputs.debtSwapPayoff,
        statedUsageCents: lineSizeCents,
        pricingBand,
        pricing,
        fixedTermMonthlyPaymentCents,
    };
}
/**
 * The line size that nets the applicant `netCashCents`: the inverse of quoteOffer, for slider UIs and the
 * one-time selection migration only. Rounds up, so the result is the smallest line whose net cash is at
 * least the target (equal to it, as a cent of line moves net cash by at most a cent), and never below the
 * offer's smallest band.
 */
function lineSizeForNetCashCents(offer, netCashCents) {
    const drawRatio = fractionOfLineDrawn(offer);
    if (!Number.isInteger(netCashCents) || netCashCents < 0) {
        fail(offer, `net cash ${netCashCents} cents must be a non-negative whole number of cents`);
    }
    const inputs = readQuoteInputs(offer);
    const netCashAt = (lineSizeCents) => computeCharges(inputs, lineSizeCents, resolvePricingBand(offer, lineSizeCents)).netCashCents;
    // Recording depends on the band, so solve each band on its own, lowest first. A non-banded offer is one range up to its own line.
    const bands = [...(offer.lineSizePricingBands ?? [])].sort((a, b) => a.minLineSize - b.minLineSize);
    for (const band of bands.length > 0 ? bands : [null]) {
        const minLineSizeCents = band ? toCents(band.minLineSize) : 1;
        const maxLineSizeCents = toCents(band ? band.maxLineSize : required(offer, 'lineSize', offer.lineSize));
        const recordingFixedCents = band?.recordingTaxFree ? 0 : inputs.recordingFixedCents;
        const recordingVariableDecimal = band?.recordingTaxFree ? 0 : inputs.recordingVariableDecimal;
        // net = line * (draw ratio * (1 - firstDraw) - origination - recordingVariable) - recordingFixed - signing - payoffs
        const netCashPerLineCent = drawRatio * (1 - inputs.firstDrawFeePercent) - inputs.originationFeePercent - recordingVariableDecimal;
        if (netCashPerLineCent <= 0) {
            fail(offer, 'fees consume the whole draw, so no line size nets cash');
        }
        const payoffsCents = inputs.refiPayoff.totalCents + inputs.debtSwapPayoff.totalCents;
        // Clamp-and-confirm: the closed form is only a seed, since per-fee cent rounding moves it a cent either way and
        // it can sit past this band's top even when the band's top itself nets enough. The forward function decides.
        const seedCents = Math.ceil((netCashCents + recordingFixedCents + inputs.effectiveSigningCents + payoffsCents) / netCashPerLineCent);
        let lineSizeCents = Math.min(maxLineSizeCents, Math.max(minLineSizeCents, seedCents));
        while (netCashAt(lineSizeCents) < netCashCents && lineSizeCents < maxLineSizeCents) {
            lineSizeCents++;
        }
        if (netCashAt(lineSizeCents) < netCashCents) {
            continue;
        }
        // Net cash grows netCashPerLineCent a line, but drops a cent where the rounded recording steps up, so a smaller line
        // can still reach the target up to 1 / netCashPerLineCent lines back.
        const lookbackCents = Math.ceil(1 / netCashPerLineCent) + 1;
        for (let stepped = true; stepped;) {
            stepped = false;
            for (let below = Math.max(minLineSizeCents, lineSizeCents - lookbackCents); below < lineSizeCents; below++) {
                if (netCashAt(below) >= netCashCents) {
                    lineSizeCents = below;
                    stepped = true;
                    break;
                }
            }
        }
        return lineSizeCents;
    }
    return fail(offer, `no line size nets ${netCashCents} cents`);
}
